import React, { createContext, useContext, useState, ReactNode, isValidElement } from "react";
import UniModal from "../../Elements/Modal";
import { ModalCommand, ModalContextState } from "../interfaces/notifications";
import { normalizeSettings } from "../helper/normalizeSettings";
import { IntlMessageValue, UniButton } from "../../Elements";

/**
 * React context providing the modal state and control functions.
 */
export const ModalContext = createContext<ModalContextState | null>(null);

/**
 * Hook to access the modal context.
 *
 * @throws Error if used outside of a {@link ModalProvider}.
 * @returns {ModalContextState} The current modal state and control functions.
 */
export const useModal = (): ModalContextState => {
    const ctx = useContext(ModalContext);
    if (!ctx) throw new Error("useModal must be used within ModalProvider");
    return ctx;
};

/**
 * Provider component that exposes modal state and functions globally.
 *
 * Wrap your application with this provider to allow any component
 * to open or close a modal using the {@link useModal} hook.
 *
 * @param {Object} props - React props.
 * @param {ReactNode} props.children - Child components that will have access to the modal context.
 * @constructor
 */
export const ModalProvider = ({ children }: { children: ReactNode }) => {
    /** The currently active modal command (payload from the API). */
    const [command, setCommand] = useState<ModalCommand | null>(null);

    /** Visibility state of the modal. */
    const [isVisible, setIsVisible] = useState(false);

    /**
     * Opens the modal with the provided command.
     *
     * @param {ModalCommand} cmd - The command object containing modal configuration.
     */
    const openModal = (cmd: ModalCommand): void => {
        setCommand(cmd);
        setIsVisible(true);
    };

    /**
     * Closes the modal and resets the command state.
     */
    const close = (): void => {
        setIsVisible(false);
        setCommand(null);
    };

    /** Normalized settings object (class, id, variant, etc.) derived from API array format. */
    const settings = normalizeSettings(command?.settings);

    return (
        <ModalContext.Provider value={{ command, isVisible, openModal, close }}>
            {children}

            <UniModal
                show={isVisible}
                setShow={setIsVisible}
                Button={{ Text: "" }}
                handleClose={close}
                className={settings.class ?? ""}
                id={settings.id ?? undefined}
                hideButton={true}
            >
                <UniModal.Body
                    elements={
                        typeof command?.content === "string"
                            ? <div dangerouslySetInnerHTML={{ __html: command?.content }} />
                            : isValidElement(command?.content)
                                ? command?.content
                                : null
                    }
                />
                <UniModal.Footer
                    elements={
                        <UniButton class={`btn btn-${settings.variant} float-end`} onClick={close}>
                            {IntlMessageValue('', 'common.close', "")}
                        </UniButton>
                    }
                />
            </UniModal>
        </ModalContext.Provider>
    );
};
